import { DataType } from "@sa/rep-common";
import { MultiplicityType, Parameter } from "@sa/rep-core";
import { useCallback, useEffect, useState } from "react";
import { combineLatest } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { useHookSubscribe } from "../../react-rxjs";
import { ServicesMaps } from "../MapReportingProvider";
import { MapDataSourceId } from "../models/MapDataSource";
import { MapExportQuery } from "../models/MapExportQuery";
import { MapAPIService } from "../services/MapAPIService";
import { MapExportQueryBuilderProvider } from "../data";
import { useMapStore } from "./useMapStore";
import { useMapTitle } from "./useMapTitle";
import { useMapSource } from "./useMapSource";
import { useService } from "./useService";
import * as L from "leaflet";

export function useMapExport(map?: L.Map) {
  const api = useService<MapAPIService>(ServicesMaps.MapAPI);
  const buildersProvider = useService<MapExportQueryBuilderProvider>(ServicesMaps.MapExportQueryBuilderProvider);
  const store = useMapStore();
  const title = useMapTitle();
  const source = useMapSource();
  const dataSource = useHookSubscribe(null, store.dataSource$);
  const dimensions = useHookSubscribe([], store.dimensions$);
  const [values, setValues] = useState<Record<string, unknown[]>>({});

  useEffect(() => {
    combineLatest(dimensions.map((dim) => store.dimensionsValues.get(dim.id)!))
      .pipe(debounceTime(300))
      .subscribe((values) => {
        setValues(
          dimensions.reduce((acc, dim, index) => {
            acc[dim.hierarchy] = [values[index]];
            return acc;
          }, {} as Record<string, unknown[]>)
        );
      });
  }, [dataSource, dimensions]);

  const handlerExport = useCallback(
    async (type: "xlsx" | "pptx") => {
      if (dataSource) {
        const exportModelBuilder = buildersProvider.resolve(dataSource.id);
        const model = await exportModelBuilder.buildExportQuery(dataSource, dimensions, { ...values });
        model.title = title;
        model.source = source;
        if (type === "pptx") {
          dimensions.forEach((dim) => {
            const parameter = model.parameters.find((e) => e.id.includes(dim.hierarchy.split(".")[0]))!;
            if (dim.visible) {
              parameter.value = values[dim.hierarchy];
            } else {
              model.parameters = model.parameters.filter((e) => e !== parameter);
            }
          });
          model.parameters = [
            {
              id: "@data_source",
              allowNullable: true,
              multiplicityType: MultiplicityType.Single,
              type: DataType.Integer,
              value: dataSource.id as number,
            },
            ...model.parameters,
          ];

          const isEU28 = Boolean(map?.["isEU28"]);
          model.parameters.push({
            id: "@eu28",
            allowNullable: true,
            multiplicityType: MultiplicityType.Single,
            type: DataType.Boolean,
            value: isEU28,
          });

          const center = map?.getCenter();
          if (center) {
            model.parameters.push({
              id: "@center",
              allowNullable: true,
              multiplicityType: MultiplicityType.Single,
              type: DataType.Text,
              value: JSON.stringify([center.lat, center.lng]),
            });
          }

          const zoom = map?.getZoom();
          if (zoom) {
            model.parameters.push({
              id: "@zoom",
              allowNullable: true,
              multiplicityType: MultiplicityType.Single,
              type: DataType.Integer,
              value: zoom as number,
            });
          }
        }

        await api.export(type, model);
      } else {
        throw new Error("UseExport: Data source is null");
      }
    },
    [dataSource, dimensions, values, title, source, map]
  );

  return handlerExport;
}
